import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import Sidebar from 'components/SignInSidebar';

import { signUpRecruiterState } from 'lib/atoms/signUpRecruiterAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';

import Company from './Company';
import styles from './Recruiter.module.scss';
import { Step1, Step2, Step3, Step4, Step5 } from './Steps';

export type CompanyData = {
  id: string;
  name: string;
  website: string;
  isNewCompany: boolean;
};

const SignUp = (): ReactElement => {
  const router = useRouter();

  const [recruiterState] = useRecoilState(signUpRecruiterState);
  const { company, step: storedStep } = recruiterState;

  const {
    isUnapprovedRecruiter,
    recruiter,
    recruiterCompany,
    isRecruiter,
    isAuthLoading,
    isInitialAuthLoading,
  } = useAuth();

  const flow_state = recruiter?.flow_state;

  const [step, setStep] = useState(storedStep);

  useEffect(() => {
    if (!recruiter) {
      setStep(storedStep);
    } else {
      if (recruiter?.user.data.attributes.is_confirmed) {
        if (!flow_state) {
          setStep(3);
        } else {
          if (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE) {
            setStep(6);
          } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_4) {
            setStep(4);
          } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_5) {
            setStep(5);
          }
        }
      } else {
        setStep(2);
      }
    }
  }, [recruiter]);

  const isNewCompany = useMemo(() => {
    return recruiterCompany?.authenticated
      ? !(
          recruiterCompany?.industry?.data?.id &&
          recruiterCompany?.icon_url &&
          recruiterCompany?.description &&
          recruiterCompany?.short_description &&
          recruiterCompany?.employees_count
        )
      : true;
  }, [recruiterCompany]);

  const progressPercentage = useMemo(() => {
    const totalSteps = isNewCompany || company?.isNewCompany ? 6 : 4;
    return Math.floor((step / totalSteps) * 100);
  }, [step, recruiterCompany, company]);

  if (isRecruiter && !isUnapprovedRecruiter) {
    if (flow_state === ONBOARDING_FLOW_STATE.COMPLETE) {
      router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
    } else if (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE) {
      router.push(PAGE_ROUTES.RECRUITER_ONBOARDING);
    }
  }

  if (
    (flow_state === ONBOARDING_FLOW_STATE.PRE_COMPLETE &&
      isUnapprovedRecruiter &&
      !isAuthLoading) ||
    step === 6
  ) {
    router.replace(PAGE_ROUTES.SIGN_UP_RECRUITER_SUCCESS);
  }

  if (isInitialAuthLoading) {
    return <></>;
  }

  return (
    <>
      <Head>
        <title>Recruiter Sign Up | Huzzle</title>
        <meta name="description" content="Sign up to Huzzle as a Recruiter." />
      </Head>
      <section className={styles.container} data-testid="Signin">
        {step === 0 && (
          <>
            <aside className={styles.sidebar}>
              <Sidebar isRecruiter />
            </aside>
            <main className={styles.main}>
              <Company className={styles.form} setStep={setStep} />
            </main>
          </>
        )}
        {step === 1 && (
          <Step1 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 2 && (
          <Step2 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 3 && (
          <Step3 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 4 && (
          <Step4 progress={progressPercentage} setStep={setStep} />
        )}
        {step === 5 && (
          <Step5 progress={progressPercentage} setStep={setStep} />
        )}
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.SIGNUP_RECRUITER, LOCALE_PAGES.SIGN_IN],
      })),
    },
  };
}

export default SignUp;
